import { useSoftphone } from '@/store/softphone';
import { useSipHealthQuery } from './useSipHealth';
import { useConnection } from '@/store/connection';

/**
 * Returns true when the agent's connection is healthy enough to receive/place
 * calls. Combines two sources of truth:
 *
 *  - Local Janus state (registered / in_call / ringing / calling = OK,
 *    idle / registering / register_failed / hangup = NOT OK)
 *  - Backend SIP health probe (3CX side IsRegistered; when PBX is unreachable
 *    we err on the side of "not healthy" so the agent timer freezes and the
 *    UI doesn't lie)
 *
 * Used by useStateTimer to pause "Login süresi / Hazır süresi" while the
 * connection is degraded — otherwise KPIs are inflated by outage minutes.
 */
export function useConnectionHealthy(): boolean {
  const sipState = useSoftphone((s) => s.state);
  const networkOffline = useSoftphone((s) => s.networkOffline);
  const pbxUnreachableStreak = useSoftphone((s) => s.pbxUnreachableStreak);
  const connStatus = useConnection((s) => s.status);
  const health = useSipHealthQuery();
  // Backend WS heartbeat down → her şeyi dondur (sip-health gate'li olduğu için
  // pbxUnreachableStreak büyümeyebilir; bu en hızlı + otoriter sinyal).
  if (connStatus === 'offline') return false;
  // Browser network is down → nothing else matters, freeze immediately
  if (networkOffline) return false;
  // navigator.onLine can lie (VPN drops while Wi-Fi stays up). Trust the
  // xapi unreachable streak — 2+ consecutive failures = real disconnect.
  if (pbxUnreachableStreak >= 2) return false;
  const stateOk = sipState === 'registered' || sipState === 'in_call'
    || sipState === 'ringing' || sipState === 'calling';
  // No health data yet (first 30s after login) → trust local state to avoid
  // freezing immediately on startup.
  if (!health) return stateOk;
  // PBX itself unreachable → don't lie about being "Hazır". Freeze.
  if (!health.pbx_reachable) return false;
  // PBX says we're not registered → mismatch, freeze until recovery.
  if (!health.registered) return false;
  return stateOk;
}
